import jwtDecode from 'jwt-decode';
import { IFlagsmith } from 'flagsmith/types';
import { EventsTrackerRepository } from 'hooks/events-tracker/useEventsTracker';
import { LoginCredential } from '../domain';
import { AuthError } from '../domain/AuthError';
import { AuthStore } from '../domain/AuthStore';
import { AuthRepository } from '../domain/AuthRepository';
import { UnifiedRegisterData } from '../domain/entity/UnifiedRegisterData';
import { useReferralCodeRepository } from '../infra/repositories/useReferralCodeRepository';
import { ReferralCodeData } from '../domain/entity/ReferralCodeData';

interface UnifiedRegisterUseCaseProps {
  authStore: AuthStore;
  authRepository: AuthRepository;
  eventsTracker: EventsTrackerRepository;
  unifiedRegisterData: UnifiedRegisterData;
  featureFlagsManager: IFlagsmith;
}

export async function unifiedRegisterUseCase({
  authStore,
  eventsTracker,
  authRepository,
  unifiedRegisterData,
  featureFlagsManager,
}: UnifiedRegisterUseCaseProps) {
  if (authStore.isLoading) return;
  authStore.startLoading();
  authStore.setAuthError(null);
  authStore.setDetailErrors([]);
  authStore.clearAuthError();

  try {
    await authRepository.registerUnififed(unifiedRegisterData);
    const loginCredential: LoginCredential = {
      email: unifiedRegisterData.email,
      password: unifiedRegisterData.password,
    };

    const accessToken = await authRepository.login(loginCredential);
    const tokenInfo: { data: any } = jwtDecode(accessToken.value);

    const { user, business } = tokenInfo.data;
    user.email = unifiedRegisterData.email;

    await featureFlagsManager.identify(business?.id);

    eventsTracker.sendEvent('NEW_USER_CREATION', {
      user,
      source: 'UNIFIED_REGISTER',
    });
    eventsTracker.sendEvent('NEW_USER_ENRICHMENT', {
      user,
      source: 'UNIFIED_REGISTER',
    });
    if (business) {
      eventsTracker.sendEvent('NEW_COMPLETED_BUSINESS_INFO_EVENT', {
        businessId: business.id,
        user,
        business,
        source: 'UNIFIED_REGISTER',
      });
    }

    const registerRedirectFlagValue = featureFlagsManager.getValue(
      'saas_register_redirect_path'
    ) as string;

    eventsTracker.sendEvent('FIRST_REGISTER_SCREEN', {
      user,
      business,
      type: registerRedirectFlagValue === 'home' ? 'home' : 'ar',
    });

    authStore.saveAccessToken(accessToken);

    if (
      unifiedRegisterData.referralCode &&
      unifiedRegisterData.referralCode !== ''
    ) {
      const referralCodeData: ReferralCodeData = {
        code: unifiedRegisterData.referralCode,
        identifier: unifiedRegisterData.identifier,
      };
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const referralCodeRepository = useReferralCodeRepository();
      await referralCodeRepository.register(referralCodeData);
    }
  } catch (error) {
    if (error instanceof AuthError) {
      authStore.setAuthError(error.message);
      authStore.setDetailErrors(error.details);
    }
  }

  authStore.stopLoading();
}
